import type { RetryableFetchParams } from './types';

// Add helper to check if an error is retryable
export function isRetryableError(error: Error): boolean {
  // Network errors, connection timeouts, and other temporary issues
  if (error instanceof RetryableAttemptError) return true;
  const lowerCaseMessage = error.message?.toLowerCase() || '';
  return (
    lowerCaseMessage.includes('network connection lost') ||
    lowerCaseMessage.includes('failed to fetch') ||
    lowerCaseMessage.includes('network request failed') ||
    lowerCaseMessage.includes('timeout') ||
    lowerCaseMessage.includes('timed out') ||
    lowerCaseMessage.includes('fetch failed') ||
    lowerCaseMessage.includes('econnrefused') ||
    lowerCaseMessage.includes('econnreset')
  );
}

export class RetryableAttemptError extends Error {
  constructor(
    message: string,
    public readonly retryAfterMs?: number,
    public readonly data?: Record<string, unknown>
  ) {
    super(message);
  }
}

export class UnretryableAttemptError extends Error {}

export class RetriedError extends Error {
  public readonly data?: Record<string, unknown>;
  constructor(
    error: Error & { data?: Record<string, unknown> },
    public readonly attempt: number
  ) {
    super(error.message);
    this.name = error.name;
    this.stack = error.stack;
    this.cause = error.cause;
    this.data = error.data;
  }
}

// Status codes that should trigger a retry
const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504, 520]);

export function isRetryableStatusCode(status: number) {
  return RETRYABLE_STATUS_CODES.has(status);
}

export const defaultAssertRetryableResponse: RetryableFetchParams['assertResponse'] =
  async ({ response, retryAfterMs }) => {
    if (isRetryableStatusCode(response.status)) {
      return {
        retryable: true,
        retryAfterMs,
        errorMessage: `Retryable HTTP status code: ${response.status}`,
        errorDetails: await formatResponse(response),
      };
    }
    if (typeof retryAfterMs === 'number') {
      return {
        retryable: true,
        retryAfterMs,
        errorMessage: `Retryable HTTP status code: ${response.status}`,
        errorDetails: await formatResponse(response),
      };
    }
  };

export async function formatResponse(
  response: Response
): Promise<Record<string, unknown>> {
  const text = await response.text();
  // Keep the readers writable: callers such as @vrplatform/graphql reassign
  // them after formatting.
  Object.defineProperty(response, 'text', {
    value: async () => text,
    writable: true,
    configurable: true,
  });
  try {
    const json = JSON.parse(text);
    Object.defineProperty(response, 'json', {
      value: async () => json,
      writable: true,
      configurable: true,
    });
    return json;
  } catch {
    return {
      text,
    };
  }
}
